import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";

import { belegDetailHref, fetchBelege } from "../api/belege";

// Controlling works through a filtered list of documents (#546). The list hands
// its order to the document it opens, so the next one is one click away instead
// of a trip back. The order travels in the router state: it belongs to this walk
// through the list, not to the document's address. A reload or a deep link has
// no list behind it, and then nothing is shown rather than something invented.
// The list snapshot covers its loaded rows. At its edge we fetch the next
// matching document with the same filters, so a walk can cross page boundaries.

export type BelegListFilters = Omit<NonNullable<Parameters<typeof fetchBelege>[0]>, "limit" | "offset">;
export type BelegListWalk = { ids: string[]; openOnly: boolean; total: number; filters: BelegListFilters | null };

function walkFrom(state: unknown): BelegListWalk | null {
  const source = state as { belegList?: unknown; belegListOpen?: unknown; belegListTotal?: unknown; belegListFilters?: unknown } | null;
  const ids = source?.belegList;
  if (!Array.isArray(ids) || ids.some((id) => typeof id !== "string")) return null;
  return {
    ids: ids as string[],
    openOnly: source?.belegListOpen === true,
    total: typeof source?.belegListTotal === "number" ? source.belegListTotal : ids.length,
    filters: source?.belegListFilters && typeof source.belegListFilters === "object"
      ? source.belegListFilters as BelegListFilters : null,
  };
}

async function followingDocument(walk: BelegListWalk): Promise<{ id: string | null; total: number }> {
  if (!walk.filters) return { id: null, total: walk.total };
  // Keep an overlap with the loaded list. A just-resolved open document may
  // have left the server result, shifting the next row before the old offset.
  const known = new Set(walk.ids);
  let offset = Math.max(0, walk.ids.length - 100);
  let candidate: string | null = null;
  let anchored = false;
  while (true) {
    const page = await fetchBelege({ ...walk.filters, offset, limit: 200 });
    for (const item of page.items) {
      if (known.has(item.beleg_id)) {
        anchored = true;
        candidate = null;
      }
      else if (candidate === null) candidate = item.beleg_id;
    }
    // If the whole overlap disappeared from the live worklist, start at its
    // beginning rather than assume the old offset still marks the boundary.
    if (!anchored && offset > 0) {
      offset = 0;
      candidate = null;
      continue;
    }
    if (candidate !== null || offset + page.items.length >= page.total || page.items.length === 0) {
      return { id: candidate, total: page.total };
    }
    offset += page.items.length;
  }
}

export default function BelegNeighbours({ belegId }: { belegId: string }) {
  const { state } = useLocation();
  const walk = walkFrom(state);
  const index = walk ? walk.ids.indexOf(belegId) : -1;
  const atLoadedEnd = Boolean(walk && index === walk.ids.length - 1);
  const more = useQuery({
    queryKey: ["beleg-next", walk?.ids.at(-1), walk?.ids.length, walk?.filters],
    queryFn: () => followingDocument(walk!),
    enabled: atLoadedEnd && Boolean(walk?.filters) && (walk?.total ?? 0) > (walk?.ids.length ?? 0),
    staleTime: 30_000,
  });
  if (!walk || index < 0) return null;
  const previous = walk.ids[index - 1];
  const next = walk.ids[index + 1] ?? more.data?.id;
  const nextState = walk.ids[index + 1] ? state : {
    ...(state as object),
    belegList: next ? [...walk.ids, next] : walk.ids,
    belegListTotal: more.data?.total ?? walk.total,
  };
  const previousLabel = walk.openOnly ? "Vorheriger offener Beleg" : "Vorheriger Beleg der Liste";
  const nextLabel = walk.openOnly ? "Nächster offener Beleg" : "Nächster Beleg der Liste";
  const step =
    "inline-flex h-8 items-center rounded-md border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900";
  const muted = "inline-flex h-8 items-center px-2.5 text-xs text-slate-400";

  return (
    <nav className="flex items-center gap-2" aria-label="Belege der Liste">
      {previous ? (
        <Link to={belegDetailHref(previous)} state={state} className={step} aria-label={previousLabel}>
          ← {walk.openOnly ? "Vorheriger offener" : "Zurück"}
        </Link>
      ) : (
        <span className={muted}>← Zurück</span>
      )}
      {next ? (
        <Link to={belegDetailHref(next)} state={nextState} className={step} aria-label={nextLabel}>
          {walk.openOnly ? "Nächster offener" : "Weiter"} →
        </Link>
      ) : more.isPending && more.fetchStatus === "fetching" ? (
        <span className={muted}>Weiter wird gesucht …</span>
      ) : more.isError ? (
        <button type="button" className={step} onClick={() => void more.refetch()}>Weiter erneut suchen</button>
      ) : (
        <span className={muted}>Weiter →</span>
      )}
    </nav>
  );
}
